import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/pages/home/components/Navbar";
import Footer from "@/pages/home/components/Footer";
import WhatsAppFloat from "@/pages/home/components/WhatsAppFloat";
import AnnouncementBar from "@/pages/home/components/AnnouncementBar";

const ABOUT_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "AboutPage",
  "name": "من نحن — متجر الإبداع AL Ebdaa",
  "description": "تعرّف على متجر الإبداع AL Ebdaa — وجهتك الأولى لأجهزة PlayStation 5 وPS4 وكمبيوتر الألعاب وإكسسوارات الألعاب الاحترافية في المملكة العربية السعودية.",
  "url": `${import.meta.env.VITE_SITE_URL ?? "https://example.com"}/about`,
  "inLanguage": "ar-SA",
  "isPartOf": {
    "@type": "WebSite",
    "name": "متجر الإبداع AL Ebdaa",
    "url": import.meta.env.VITE_SITE_URL ?? "https://example.com"
  },
  "about": {
    "@type": "Store",
    "name": "متجر الإبداع AL Ebdaa",
    "description": "متجر متخصص في بيع أجهزة الألعاب والإكسسوارات الاحترافية في المملكة العربية السعودية",
    "address": {
      "@type": "PostalAddress",
      "streetAddress": "25 main.st, Al Nuzhah",
      "addressLocality": "Taif",
      "postalCode": "26311",
      "addressCountry": "SA"
    },
    "geo": {
      "@type": "GeoCoordinates",
      "latitude": 21.2854,
      "longitude": 40.4167
    },
    "hasMap": "https://maps.app.goo.gl/E5Cqt2ggrmYcHYsn9",
    "areaServed": { "@type": "Country", "name": "Saudi Arabia" },
    "currenciesAccepted": "SAR",
    "paymentAccepted": "Cash, Bank Transfer"
  }
};

const stats = [
  { value: "+5000", label: "عميل سعيد", icon: "ri-user-smile-line" },
  { value: "+200", label: "منتج متاح", icon: "ri-store-2-line" },
  { value: "4.9", label: "تقييم العملاء", icon: "ri-star-fill" },
  { value: "24/7", label: "دعم فني", icon: "ri-customer-service-2-line" },
];

const values = [
  {
    icon: "ri-shield-check-line",
    title: "الجودة أولاً",
    desc: "نختار كل منتج بعناية فائقة لضمان أعلى معايير الجودة. كل جهاز وإكسسوار يمر بفحص دقيق قبل وصوله إليك.",
  },
  {
    icon: "ri-price-tag-3-line",
    title: "أسعار تنافسية",
    desc: "نؤمن بأن الجودة لا تعني بالضرورة السعر المرتفع. نعمل باستمرار على تقديم أفضل الأسعار في السوق السعودي.",
  },
  {
    icon: "ri-truck-line",
    title: "توصيل سريع وموثوق",
    desc: "نوصّل طلباتك لجميع أنحاء المملكة العربية السعودية بسرعة وأمان، مع إمكانية تتبع طلبك لحظة بلحظة.",
  },
  {
    icon: "ri-exchange-line",
    title: "سياسة إرجاع مرنة",
    desc: "راحتك تهمنا. نتيح لك إرجاع أي منتج خلال 14 يوماً من الاستلام دون أي تعقيدات.",
  },
  {
    icon: "ri-vip-crown-line",
    title: "برنامج مكافآت حصري",
    desc: "اكسب نقاطاً مع كل عملية شراء وارتقِ بمستواك من برونزي إلى بلاتيني. استمتع بخصومات وامتيازات حصرية.",
  },
  {
    icon: "ri-whatsapp-line",
    title: "دعم عبر واتساب",
    desc: "فريق خدمة العملاء متاح على مدار الساعة عبر واتساب للإجابة على جميع استفساراتك وحل أي مشكلة.",
  },
];

const team = [
  {
    name: "فريق المبيعات",
    role: "خبراء الألعاب",
    desc: "فريق متخصص من عشاق الألعاب يساعدك في اختيار المنتج المناسب لاحتياجاتك.",
    icon: "ri-gamepad-line",
    color: "text-amber-400",
    bg: "bg-amber-400/10",
    border: "border-amber-400/20",
  },
  {
    name: "فريق الدعم الفني",
    role: "متخصصو الصيانة",
    desc: "خبراء تقنيون يقدمون الدعم الفني والمساعدة في إعداد الأجهزة وحل المشكلات.",
    icon: "ri-tools-line",
    color: "text-green-400",
    bg: "bg-green-400/10",
    border: "border-green-400/20",
  },
  {
    name: "فريق التوصيل",
    role: "شركاء الشحن",
    desc: "شبكة توصيل موثوقة تضمن وصول طلبك بأمان وفي الوقت المحدد لأي مكان في المملكة.",
    icon: "ri-truck-line",
    color: "text-sky-400",
    bg: "bg-sky-400/10",
    border: "border-sky-400/20",
  },
];

const milestones = [
  { year: "2019", title: "تأسيس المتجر", desc: "انطلقنا بشغف حقيقي لتوفير أفضل منتجات الألعاب للسوق السعودي." },
  { year: "2021", title: "إطلاق PS5", desc: "كنا من أوائل المتاجر في المملكة التي وفّرت PlayStation 5 عند إطلاقه." },
  { year: "2023", title: "برنامج المكافآت", desc: "أطلقنا برنامج نقاط المكافآت لتقدير عملائنا الأوفياء." },
  { year: "2024", title: "+5000 عميل", desc: "تجاوزنا حاجز 5000 عميل سعيد في جميع أنحاء المملكة." },
  { year: "2026", title: "التوسع المستمر", desc: "نواصل توسيع كتالوجنا وتحسين تجربة التسوق لعملائنا." },
];

export default function AboutPage() {
  const [barVisible, setBarVisible] = useState(true);
  const handleBarVisibility = useCallback((v: boolean) => setBarVisible(v), []);

  useEffect(() => {
    const script = document.createElement("script");
    script.type = "application/ld+json";
    script.id = "schema-about";
    script.textContent = JSON.stringify(ABOUT_SCHEMA);
    document.head.appendChild(script);
    document.title = "من نحن — متجر الإبداع AL Ebdaa | أجهزة ألعاب PS5 وPS4";
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) {
      metaDesc.setAttribute(
        "content",
        "تعرّف على متجر الإبداع AL Ebdaa — وجهتك الأولى لأجهزة PlayStation 5 وPS4 وكمبيوتر الألعاب وإكسسوارات الألعاب الاحترافية في المملكة العربية السعودية. جودة عالية، أسعار تنافسية، توصيل سريع."
      );
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
    return () => {
      const el = document.getElementById("schema-about");
      if (el) el.remove();
    };
  }, []);

  return (
    <div dir="rtl" className="bg-[#0a0a0a] min-h-screen" style={{ fontFamily: "'Cairo', sans-serif" }}>
      <AnnouncementBar onVisibilityChange={handleBarVisibility} />
      <Navbar barVisible={barVisible} />

      <main>
        {/* ── Hero ── */}
        <section className="relative pt-40 pb-24 px-4 overflow-hidden">
          {/* Background glows */}
          <div className="absolute inset-0 pointer-events-none overflow-hidden">
            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[700px] h-[400px] bg-amber-400/6 rounded-full blur-3xl" />
            <div className="absolute top-20 right-0 w-[300px] h-[300px] bg-amber-400/4 rounded-full blur-3xl" />
          </div>

          <div className="relative max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-amber-400/10 border border-amber-400/20 rounded-full px-4 py-1.5 mb-6">
              <i className="ri-store-2-line text-amber-400 text-sm"></i>
              <span className="text-amber-400 text-xs font-bold tracking-widest uppercase">متجر الإبداع</span>
            </div>

            <h1 className="text-4xl md:text-6xl font-black text-white mb-6 leading-tight">
              نحن نعيش الألعاب
              <span className="block text-amber-400 mt-1">ونشاركها معك</span>
            </h1>

            <p className="text-gray-400 text-base md:text-lg leading-relaxed max-w-2xl mx-auto mb-10">
              منذ عام 2019، ونحن نسعى لتوفير أفضل تجربة تسوق لمنتجات الألعاب في المملكة العربية السعودية.
              من PlayStation 5 إلى أيادي التحكم والسماعات الاحترافية — كل شيء بجودة مضمونة وسعر منافس.
            </p>

            <div className="flex flex-wrap items-center justify-center gap-4">
              <Link
                to="/products"
                className="flex items-center gap-2 bg-amber-400 hover:bg-amber-300 text-black font-black px-7 py-3.5 rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-shopping-bag-line"></i>
                تسوّق الآن
              </Link>
              <a
                href="https://wa.me/966541138847"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 bg-[#25D366]/10 hover:bg-[#25D366]/20 border border-[#25D366]/30 text-[#25D366] font-bold px-7 py-3.5 rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap"
              >
                <i className="ri-whatsapp-line"></i>
                تواصل معنا
              </a>
            </div>
          </div>
        </section>

        {/* ── Stats ── */}
        <section className="px-4 pb-20">
          <div className="max-w-5xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-4">
            {stats.map((s) => (
              <div
                key={s.label}
                className="bg-[#111] border border-white/5 rounded-2xl p-6 text-center hover:border-amber-400/20 transition-all"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-amber-400/10 rounded-xl mx-auto mb-3">
                  <i className={`${s.icon} text-amber-400 text-2xl`}></i>
                </div>
                <p className="text-white text-3xl font-black mb-1">{s.value}</p>
                <p className="text-gray-500 text-sm">{s.label}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── Story ── */}
        <section className="px-4 pb-24">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
              {/* Image */}
              <div className="relative">
                <div className="rounded-2xl overflow-hidden">
                  <img
                    src="https://public.readdy.ai/ai/img_res/08392f1ab232514d522b8794b8df9983.jpg"
                    alt="متجر الإبداع — بيئة التسوق"
                    className="w-full h-[380px] object-cover object-top"
                  />
                </div>
                {/* Floating badge */}
                <div className="absolute -bottom-5 -left-5 bg-amber-400 text-black rounded-2xl px-5 py-3 font-black text-sm">
                  <p className="text-2xl font-black leading-none">+7</p>
                  <p className="text-xs font-bold mt-0.5">سنوات خبرة</p>
                </div>
              </div>

              {/* Text */}
              <div>
                <p className="text-amber-400 text-xs tracking-widest uppercase mb-3 font-bold">قصتنا</p>
                <h2 className="text-3xl md:text-4xl font-black text-white mb-5 leading-tight">
                  من شغف الألعاب
                  <span className="block text-gray-400 text-2xl font-bold mt-1">إلى متجر يثق به الآلاف</span>
                </h2>
                <div className="space-y-4 text-gray-400 text-sm leading-relaxed">
                  <p>
                    بدأت قصة متجر الإبداع من شغف حقيقي بعالم الألعاب الإلكترونية. أدركنا أن السوق السعودي يحتاج إلى متجر متخصص يجمع بين الجودة العالية والأسعار المنافسة وخدمة العملاء الاستثنائية.
                  </p>
                  <p>
                    اليوم، نفخر بخدمة أكثر من 5000 عميل في جميع أنحاء المملكة العربية السعودية، ونقدم أحدث أجهزة PlayStation 5 وPS4 وكمبيوتر الألعاب وإكسسوارات الألعاب الاحترافية.
                  </p>
                  <p>
                    رسالتنا بسيطة: نريد أن تحصل على أفضل تجربة ألعاب ممكنة، بدءاً من لحظة تصفحك لمنتجاتنا وحتى وصول طلبك إلى بابك.
                  </p>
                </div>

                <div className="flex flex-wrap gap-3 mt-7">
                  {["PS5 متخصصون", "ضمان أصلي", "توصيل سريع", "دعم 24/7"].map((tag) => (
                    <span
                      key={tag}
                      className="flex items-center gap-1.5 bg-amber-400/8 border border-amber-400/20 text-amber-400 text-xs font-bold px-3 py-1.5 rounded-full"
                    >
                      <i className="ri-check-line text-xs"></i>
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── Values ── */}
        <section className="px-4 pb-24 bg-[#080808]">
          <div className="max-w-6xl mx-auto pt-20">
            <div className="text-center mb-14">
              <p className="text-amber-400 text-xs tracking-widest uppercase mb-3 font-bold">ما يميزنا</p>
              <h2 className="text-3xl md:text-4xl font-black text-white">قيمنا ومبادئنا</h2>
              <p className="text-gray-500 text-sm mt-3 max-w-lg mx-auto">
                كل قرار نتخذه يرتكز على هذه القيم الأساسية التي تجعل تجربتك معنا استثنائية
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {values.map((v) => (
                <div
                  key={v.title}
                  className="bg-[#111] border border-white/5 rounded-2xl p-6 hover:border-amber-400/20 transition-all group"
                >
                  <div className="w-12 h-12 flex items-center justify-center bg-amber-400/10 rounded-xl mb-4 group-hover:bg-amber-400/20 transition-all">
                    <i className={`${v.icon} text-amber-400 text-2xl`}></i>
                  </div>
                  <h3 className="text-white font-black text-base mb-2">{v.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Timeline ── */}
        <section className="px-4 py-24">
          <div className="max-w-3xl mx-auto">
            <div className="text-center mb-14">
              <p className="text-amber-400 text-xs tracking-widest uppercase mb-3 font-bold">رحلتنا</p>
              <h2 className="text-3xl md:text-4xl font-black text-white">محطات بارزة</h2>
            </div>

            <div className="relative">
              {/* Vertical line */}
              <div className="absolute right-[19px] top-0 bottom-0 w-px bg-gradient-to-b from-amber-400/40 via-amber-400/20 to-transparent" />

              <div className="flex flex-col gap-8">
                {milestones.map((m, i) => (
                  <div key={m.year} className="flex gap-6 items-start">
                    {/* Dot */}
                    <div className="relative flex-shrink-0">
                      <div className={`w-10 h-10 flex items-center justify-center rounded-full border-2 z-10 relative ${
                        i === milestones.length - 1
                          ? "bg-amber-400 border-amber-400 text-black"
                          : "bg-[#111] border-amber-400/40 text-amber-400"
                      }`}>
                        <i className="ri-map-pin-line text-sm"></i>
                      </div>
                    </div>
                    {/* Content */}
                    <div className="flex-1 bg-[#111] border border-white/5 rounded-2xl p-5 hover:border-amber-400/20 transition-all">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-amber-400 font-black text-lg">{m.year}</span>
                        <h3 className="text-white font-bold text-sm">{m.title}</h3>
                      </div>
                      <p className="text-gray-500 text-sm leading-relaxed">{m.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* ── Team ── */}
        <section className="px-4 pb-24 bg-[#080808]">
          <div className="max-w-5xl mx-auto pt-20">
            <div className="text-center mb-14">
              <p className="text-amber-400 text-xs tracking-widest uppercase mb-3 font-bold">فريقنا</p>
              <h2 className="text-3xl md:text-4xl font-black text-white">من يقف وراء الإبداع</h2>
              <p className="text-gray-500 text-sm mt-3 max-w-lg mx-auto">
                فريق متكامل من المتخصصين يعمل بشغف لتقديم أفضل تجربة تسوق ممكنة
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {team.map((t) => (
                <div
                  key={t.name}
                  className={`bg-[#111] border ${t.border} rounded-2xl p-6 text-center hover:scale-[1.02] transition-all`}
                >
                  <div className={`w-16 h-16 flex items-center justify-center ${t.bg} rounded-2xl mx-auto mb-4`}>
                    <i className={`${t.icon} ${t.color} text-3xl`}></i>
                  </div>
                  <h3 className="text-white font-black text-base mb-1">{t.name}</h3>
                  <p className={`${t.color} text-xs font-bold mb-3`}>{t.role}</p>
                  <p className="text-gray-500 text-sm leading-relaxed">{t.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* ── Map ── */}
        <section className="px-4 pb-16">
          <div className="max-w-3xl mx-auto">
            <div className="bg-[#111] border border-white/5 rounded-2xl overflow-hidden">
              <div className="px-6 py-4 flex items-center justify-between border-b border-white/5">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 flex items-center justify-center bg-amber-400/10 rounded-xl">
                    <i className="ri-map-pin-2-fill text-amber-400 text-base"></i>
                  </div>
                  <div>
                    <p className="text-white font-bold text-sm" style={{ fontFamily: "'Cairo', sans-serif" }}>موقعنا</p>
                    <p className="text-gray-500 text-xs" style={{ fontFamily: "'Cairo', sans-serif" }}>شارع الرئيسي 25، النزهة، الطائف 26311</p>
                  </div>
                </div>
                <a
                  href="https://maps.app.goo.gl/E5Cqt2ggrmYcHYsn9"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 bg-amber-400/10 hover:bg-amber-400/20 border border-amber-400/20 text-amber-400 text-xs font-bold px-3 py-2 rounded-xl transition-all cursor-pointer whitespace-nowrap"
                  style={{ fontFamily: "'Cairo', sans-serif" }}
                >
                  <i className="ri-external-link-line text-xs"></i>
                  فتح في قوقل ماب
                </a>
              </div>
              <iframe
                title="موقع متجر الإبداع على خرائط قوقل"
                src="https://maps.google.com/maps?q=al-Ebdaa+%D8%A7%D9%84%D8%A7%D8%A8%D8%AF%D8%A7%D8%B9+%D9%84%D9%84%D8%A7%D9%84%D9%83%D8%AA%D8%B1%D9%88%D9%86%D9%8A%D8%A7%D8%AA+%D9%88+%D8%A7%D9%84%D8%AD%D8%A7%D8%B3%D8%A8,+25+main.st,+Al+Nuzhah,+Taif+26311,+Saudi+Arabia&t=&z=17&ie=UTF8&iwloc=&output=embed"
                width="100%"
                height="320"
                style={{ border: 0, display: "block" }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </div>
        </section>

        {/* ── CTA ── */}
        <section className="px-4 py-24">
          <div className="max-w-3xl mx-auto">
            <div className="relative bg-gradient-to-br from-amber-400/15 via-amber-400/5 to-transparent border border-amber-400/25 rounded-3xl p-10 text-center overflow-hidden">
              <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-amber-400/0 via-amber-400/60 to-amber-400/0" />
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[200px] bg-amber-400/5 rounded-full blur-3xl" />
              </div>

              <div className="relative">
                <div className="w-16 h-16 flex items-center justify-center bg-amber-400/15 border border-amber-400/25 rounded-2xl mx-auto mb-5">
                  <i className="ri-gamepad-line text-amber-400 text-3xl"></i>
                </div>
                <h2 className="text-3xl font-black text-white mb-3">جاهز للعب؟</h2>
                <p className="text-gray-400 text-sm mb-8 max-w-md mx-auto leading-relaxed">
                  تصفّح أحدث منتجاتنا من أجهزة PlayStation 5 وPS4 وكمبيوتر الألعاب وإكسسوارات الألعاب الاحترافية
                </p>
                <div className="flex flex-wrap items-center justify-center gap-4">
                  <Link
                    to="/products"
                    className="flex items-center gap-2 bg-amber-400 hover:bg-amber-300 text-black font-black px-8 py-3.5 rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-shopping-bag-line"></i>
                    تسوّق الآن
                  </Link>
                  <Link
                    to="/deals"
                    className="flex items-center gap-2 border border-amber-400/30 hover:border-amber-400/60 text-amber-400 font-bold px-8 py-3.5 rounded-xl text-sm transition-all cursor-pointer whitespace-nowrap"
                  >
                    <i className="ri-flashlight-line"></i>
                    العروض الحصرية
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}